import { ILoginCredentials } from "@/interfaces/auth.interface";
import { setUser } from "@/store/slices/userSlice";
import { store } from "@/store/store";
import { auth } from "@/lib/auth";
import ToastService from "@/utils/toast";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { api } from "@/lib/api";
import { ApiError, ApiResponse } from "@/interfaces/api.interface";
import { IUser } from "@/interfaces/user.interface";
import { clearLocalStorageExcept } from "@/utils/common";
class AuthService {
  async refreshToken() {
    try {
      store.dispatch(showLoader());
      const token = localStorage.getItem("rtToken");

      if (!token) {
        clearLocalStorageExcept([
          "selectedProjectId",
          "selectedLevel",
          "selectedUsersFilter",
          "selectedStatus",
          "loginUser",
        ]);
        window.location.reload();
      }

      const res: any = await auth.post(
        `${process.env.REACT_APP_BASE_URL}/Auth/refresh`,
        { refreshToken: token }
      );

      if (res.ok && res.data) {
        store.dispatch(setUser(res.data));
        return { data: res.data, error: null };
      } else {
        clearLocalStorageExcept([
          "selectedProjectId",
          "selectedLevel",
          "selectedUsersFilter",
          "selectedStatus",
          "loginUser",
        ]);
      }

      return { data: null, error: res.data };
    } catch (error) {
      return { data: null, error: error };
    } finally {
      store.dispatch(hideLoader());
    }
  }

  async login(dataItem: { [name: string]: ILoginCredentials }) {
    try {
      store.dispatch(showLoader());
      const res = await auth.post(
        `${process.env.REACT_APP_BASE_URL}/Auth/login`,
        {
          email: dataItem.email,
          password: dataItem.password,
        }
      );

      if (res.ok) {
        return { data: res.data, error: null };
      }

      return { data: null, error: res.data };
    } catch (error) {
      return { data: null, error: error };
    } finally {
      store.dispatch(hideLoader());
    }
  }

  async forgotPassword(email: string) {
    try {
      store.dispatch(showLoader());
      const res = await auth.post(
        `${process.env.REACT_APP_BASE_URL}/Auth/forgot-password`,
        {
          email: email,
        }
      );

      if (res.ok) {
        return { data: res.data, error: null };
      }

      return { data: null, error: res.data };
    } catch (error) {
      return { data: null, error: error };
    } finally {
      store.dispatch(hideLoader());
    }
  }

  async resetPassword(token: string, newPassword: string) {
    try {
      store.dispatch(showLoader());

      const res = await auth.put(
        `${process.env.REACT_APP_BASE_URL}/Auth/reset-password`,
        {
          token,
          newPassword,
        }
      );

      if (res.ok) {
        return { data: res.data, error: null };
      }

      return { data: null, error: res.data };
    } catch (error) {
      return { data: null, error: error };
    } finally {
      store.dispatch(hideLoader());
    }
  }

  async changePassword(oldPassword: string, newPassword: string) {
    try {
      store.dispatch(showLoader());

      const res = await api.put(
        `${process.env.REACT_APP_BASE_URL}/Auth/change-password`,
        {
          oldPassword,
          newPassword,
        }
      );

      if (res.ok) {
        return { data: { success: true }, error: null };
      }

      return { data: null, error: res.data };
    } catch (error) {
      return { data: null, error: error };
    } finally {
      store.dispatch(hideLoader());
    }
  }

  async verifyToken(token: string) {
    try {
      store.dispatch(showLoader());

      const res = await auth.post(
        `${process.env.REACT_APP_BASE_URL}/Auth/token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.ok) {
        return { data: res.data, error: null };
      }

      return { data: null, error: res.data };
    } catch (error) {
      return { data: null, error: error };
    } finally {
      store.dispatch(hideLoader());
    }
  }
  async createUser(payload: any): Promise<ApiResponse<IUser>> {
    try {
      store.dispatch(showLoader());
      const res = await auth.post(
        `${process.env.REACT_APP_BASE_URL}/Auth/register`,
        payload
      );

      if (res.ok) {
        return { data: res.data as IUser, error: null };
      }
      ToastService.error(res.data as string);
      return { data: null, error: res.data as ApiError };
    } catch (error) {
      return { data: null, error: error as ApiError };
    } finally {
      store.dispatch(hideLoader());
    }
  }
}

const authService = new AuthService();

export default authService;
